import { Sparkles } from "lucide-react";
import { twMerge } from "tailwind-merge";

import type { MBCUIMessage } from "../types/mbc-chat.types";
import type { MbcUploadBinding } from "./parts/UploadPromptPart";

import { MBC_MASTRA_START_MESSAGE } from "../config/mbc-chat.config";
import { BrandSelectPart } from "./parts/BrandSelectPart";
import { ChoicesPart } from "./parts/ChoicesPart";
import { ReviewBriefCTA } from "./parts/ReviewBriefCTA";
import { TextPart } from "./parts/TextPart";
import { UploadPromptPart } from "./parts/UploadPromptPart";

type MBCMessageBubbleProps = {
  isInteractiveDisabled: boolean;
  isLastAssistantMessage: boolean;
  message: MBCUIMessage;
  onReview: () => void;
  onSelectChoice: (label: string) => void;
  upload?: MbcUploadBinding;
};

export function MBCMessageBubble({
  isInteractiveDisabled,
  isLastAssistantMessage,
  message,
  onReview,
  onSelectChoice,
  upload,
}: MBCMessageBubbleProps) {
  const isUser = message.role === "user";

  const visibleText = message.parts
    .filter((part) => part.type === "text")
    .map((part) => ("text" in part ? part.text : ""))
    .join("")
    .trim();

  // The kickoff message is sent to the agent but never shown in the transcript.
  if (isUser && (!visibleText || visibleText === MBC_MASTRA_START_MESSAGE))
    return null;

  // The upload block is the one part that renders outside the bubble: it is a
  // full-width panel in the design, not something that reads as speech.
  // `.at(-1)`: if the agent called the tool twice in one turn, both parts are
  // on the message — the newest one is the one that means anything.
  const uploadPrompt = (
    !isUser && isLastAssistantMessage
      ? message.parts.flatMap((part) =>
          part.type === "data-uploadPrompt" ? [part.data] : [],
        )
      : []
  ).at(-1);

  return (
    <div className="flex w-full flex-col gap-md">
      <div
        className={twMerge(
          "flex w-full gap-md",
          isUser ? "justify-end" : "justify-start",
        )}
      >
        {isUser ? null : (
          <div className="mt-xs flex size-8 shrink-0 items-center justify-center rounded-full bg-utility-brand-100 text-fg-brand-primary">
            <Sparkles className="size-4" />
          </div>
        )}

        <div
          className={twMerge(
            "max-w-[85%] rounded-2xl px-lg py-md",
            isUser
              ? "bg-utility-brand-700 text-white"
              : "border border-border-secondary bg-bg-primary text-text-primary",
          )}
        >
          {visibleText ? <TextPart text={visibleText} /> : null}

          {!isUser && isLastAssistantMessage
            ? message.parts.map((part, index) => {
                if (part.type === "data-choices") {
                  return (
                    <ChoicesPart
                      choices={part.data}
                      isDisabled={isInteractiveDisabled}
                      key={`choices-${index}`}
                      onSelect={onSelectChoice}
                    />
                  );
                }

                if (part.type === "data-brandSelect") {
                  return (
                    <BrandSelectPart
                      brandSelect={part.data}
                      isDisabled={isInteractiveDisabled}
                      key={`brand-${index}`}
                      onSelect={onSelectChoice}
                    />
                  );
                }

                if (part.type === "data-reviewCta") {
                  return (
                    <ReviewBriefCTA
                      cta={part.data}
                      isDisabled={isInteractiveDisabled}
                      key={`review-${index}`}
                      onReview={onReview}
                    />
                  );
                }

                return null;
              })
            : null}
        </div>
      </div>

      {uploadPrompt ? (
        <UploadPromptPart
          isDisabled={isInteractiveDisabled}
          onSelectChoice={onSelectChoice}
          prompt={uploadPrompt}
          upload={upload}
        />
      ) : null}
    </div>
  );
}
